import React, { useEffect, useState, useRef, useCallback } from 'react';
import { X, Share, Plus, Smartphone, MoreVertical, Home } from 'lucide-react';
import { logger } from '../lib/logger';

/* InstallPromptBanner — Phase A round 1
 *
 * Educates and nudges visitors to pin Apex Athletic Mind to their phone's
 * home screen. Handles three flavours:
 *
 *   • iOS Safari: no native install API exists, so we render a clean visual
 *     walkthrough modal ("tap the Share icon → Add to Home Screen").
 *   • Android Chrome / Edge: we capture the `beforeinstallprompt` event and
 *     defer it, then trigger it from our own branded CTA.
 *   • Desktop Chrome/Edge: same `beforeinstallprompt` path, just with
 *     desktop-specific copy.
 *
 * Trigger rule (intentionally restrained):
 *   Show only after the visitor's THIRD distinct page load OR after they
 *   land on a high-intent page (e.g. /quiz, /assessment, /app/login).
 *   Once dismissed (X) the banner never returns. Once dismissed-with-help
 *   (the "Maybe later" link), it returns after 14 days.
 */

const DISMISSED_FOREVER_KEY = 'apex.installPrompt.dismissed.v1';
const SNOOZED_UNTIL_KEY = 'apex.installPrompt.snoozedUntil.v1';
const VISIT_COUNT_KEY = 'apex.installPrompt.visits.v1';
const HIGH_INTENT_ROUTES = ['/quiz', '/assessment', '/app/login', '/locker-room', '/method'];

const isInStandaloneMode = () => {
  if (typeof window === 'undefined') return false;
  // iOS Safari uses navigator.standalone, modern browsers use the media query.
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator?.standalone === true
  );
};

const detectPlatform = () => {
  if (typeof window === 'undefined') return 'unknown';
  const ua = window.navigator.userAgent || '';
  const platform = window.navigator.platform || '';
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (platform === 'MacIntel' && (window.navigator.maxTouchPoints || 0) > 1);
  if (isIOS) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
};

const safeRead = (key) => {
  try {
    return typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
  } catch (e) {
    logger.debug('InstallPromptBanner read blocked', e);
    return null;
  }
};

const safeWrite = (key, value) => {
  try {
    if (typeof window !== 'undefined') window.localStorage.setItem(key, value);
  } catch (e) {
    logger.debug('InstallPromptBanner write blocked', e);
  }
};

export const InstallPromptBanner = () => {
  const [visible, setVisible] = useState(false);
  const [walkthroughOpen, setWalkthroughOpen] = useState(false);
  const [platform, setPlatform] = useState('unknown');
  const deferredPromptRef = useRef(null);

  // -------------------------------------------------------------------------
  // First-load gate: count visits + decide whether to show.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (isInStandaloneMode()) return undefined; // already installed
    if (safeRead(DISMISSED_FOREVER_KEY) === '1') return undefined;

    const snoozed = parseInt(safeRead(SNOOZED_UNTIL_KEY) || '0', 10);
    if (snoozed && Date.now() < snoozed) return undefined;

    // Bump visit counter.
    const count = parseInt(safeRead(VISIT_COUNT_KEY) || '0', 10) + 1;
    safeWrite(VISIT_COUNT_KEY, String(count));

    setPlatform(detectPlatform());

    const path = window.location.pathname || '/';
    const isHighIntent = HIGH_INTENT_ROUTES.some((r) => path.startsWith(r));
    if (count < 3 && !isHighIntent) return undefined;

    // Slight delay so the banner doesn't fight first-paint UX.
    const timer = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  // -------------------------------------------------------------------------
  // Android / desktop Chrome — capture the beforeinstallprompt event so we
  // can trigger it from our own button later.
  // -------------------------------------------------------------------------
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismissForever = useCallback(() => {
    safeWrite(DISMISSED_FOREVER_KEY, '1');
    setVisible(false);
  }, []);

  const snoozeTwoWeeks = useCallback(() => {
    const until = Date.now() + 14 * 24 * 60 * 60 * 1000;
    safeWrite(SNOOZED_UNTIL_KEY, String(until));
    setVisible(false);
    setWalkthroughOpen(false);
  }, []);

  const handleInstallClick = useCallback(async () => {
    if (platform === 'ios') {
      setWalkthroughOpen(true);
      return;
    }
    const deferred = deferredPromptRef.current;
    if (!deferred) {
      // Fall back to the walkthrough modal even on Android if the prompt
      // isn't available (could happen if the visitor already triggered it
      // earlier in the session).
      setWalkthroughOpen(true);
      return;
    }
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome === 'accepted') {
        dismissForever();
      } else {
        snoozeTwoWeeks();
      }
    } catch (e) {
      logger.debug('InstallPromptBanner prompt failed', e);
      setWalkthroughOpen(true);
    } finally {
      deferredPromptRef.current = null;
    }
  }, [platform, dismissForever, snoozeTwoWeeks]);

  if (!visible) return null;

  return (
    <>
      {/* Bottom banner */}
      <div
        role="dialog"
        aria-label="Install Apex Athletic Mind"
        data-testid="install-prompt-banner"
        className="fixed inset-x-0 bottom-0 z-[85] px-4 pb-4 pointer-events-none"
      >
        <div className="mx-auto max-w-[640px] pointer-events-auto rounded-2xl border border-[#004C54]/20 bg-[#0B0C0D] text-white shadow-2xl shadow-black/40 p-4 sm:p-5">
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="hidden sm:flex shrink-0 h-11 w-11 rounded-xl bg-[#004C54]/15 border border-[#004C54]/30 items-center justify-center">
              <Smartphone className="h-5 w-5 text-[#4FBCC7]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-heading font-bold text-white leading-tight">
                Put Coach Lewis on your home screen.
              </div>
              <p className="text-[12.5px] text-white/65 mt-1 leading-snug">
                Open the app one tap from your lock screen. Pre-game cues, quick mindset help, no app store needed.
              </p>
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={handleInstallClick}
                  className="inline-flex items-center justify-center rounded-full bg-[#004C54] hover:bg-[#003a40] text-white text-[12.5px] font-semibold px-4 h-9 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4FBCC7] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0C0D]"
                  data-testid="install-prompt-cta"
                >
                  {platform === 'ios' ? 'Show me how' : 'Install'}
                </button>
                <button
                  type="button"
                  onClick={snoozeTwoWeeks}
                  className="text-[12px] text-white/55 hover:text-white/85"
                  data-testid="install-prompt-later"
                >
                  Maybe later
                </button>
                <a
                  href="/install"
                  className="text-[12px] text-white/45 hover:text-white/85 ml-auto"
                  data-testid="install-prompt-learn-more"
                >
                  Learn more &rarr;
                </a>
              </div>
            </div>
            <button
              type="button"
              onClick={dismissForever}
              aria-label="Dismiss install prompt"
              data-testid="install-prompt-dismiss"
              className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-full text-white/35 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* iOS Safari visual walkthrough */}
      {walkthroughOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6"
          onClick={() => setWalkthroughOpen(false)}
          data-testid="install-walkthrough-overlay"
        >
          <div
            className="w-full max-w-[440px] bg-white rounded-2xl shadow-2xl p-5 sm:p-6 max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="install-walkthrough-title"
            data-testid="install-walkthrough-modal"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] font-mono-accent uppercase tracking-[0.2em] text-[#004C54] font-bold">
                  10-second setup
                </div>
                <h2
                  id="install-walkthrough-title"
                  className="font-heading text-[18px] sm:text-[20px] font-bold text-foreground mt-1 leading-tight"
                >
                  {platform === 'ios'
                    ? 'Pin Apex Mind on iPhone'
                    : 'Pin Apex Mind on your phone'}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setWalkthroughOpen(false)}
                aria-label="Close walkthrough"
                data-testid="install-walkthrough-close"
                className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-full text-foreground/40 hover:text-foreground hover:bg-secondary"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <ol className="mt-5 space-y-4">
              {(platform === 'ios'
                ? [
                    {
                      icon: <Share className="h-4 w-4 text-[#004C54]" />,
                      title: 'Tap the Share icon',
                      body: 'Look at the bottom of Safari for the square with an arrow pointing up. That\u2019s the Share button.',
                    },
                    {
                      icon: <Plus className="h-4 w-4 text-[#004C54]" />,
                      title: 'Scroll down, pick "Add to Home Screen"',
                      body: 'You\u2019ll see a list of options. Apex Mind will give you a custom icon.',
                    },
                    {
                      icon: <Home className="h-4 w-4 text-[#004C54]" />,
                      title: 'Tap "Add"',
                      body: 'The Apex icon now lives on your home screen. Tap it any time \u2014 it opens full-screen, no Safari chrome.',
                    },
                  ]
                : [
                    {
                      icon: <MoreVertical className="h-4 w-4 text-[#004C54]" />,
                      title: 'Tap the 3-dot menu in Chrome',
                      body: 'Top-right corner of the browser, on the toolbar.',
                    },
                    {
                      icon: <Plus className="h-4 w-4 text-[#004C54]" />,
                      title: 'Pick "Install app" or "Add to Home screen"',
                      body: 'The wording depends on your phone, but the action is the same.',
                    },
                    {
                      icon: <Home className="h-4 w-4 text-[#004C54]" />,
                      title: 'Confirm. You\u2019re done.',
                      body: 'Apex Mind now opens like a real app from your home screen.',
                    },
                  ]).map((step, idx) => (
                <li
                  key={`step-${idx}`}
                  className="flex gap-3"
                  data-testid={`install-walkthrough-step-${idx + 1}`}
                >
                  <div className="shrink-0 h-8 w-8 rounded-full bg-[#004C54]/10 border border-[#004C54]/20 inline-flex items-center justify-center">
                    {step.icon}
                  </div>
                  <div className="flex-1">
                    <div className="text-[13.5px] font-semibold text-foreground leading-snug">
                      {idx + 1}. {step.title}
                    </div>
                    <p className="text-[12.5px] text-foreground/65 leading-snug mt-1">
                      {step.body}
                    </p>
                  </div>
                </li>
              ))}
            </ol>

            <div className="mt-6 pt-4 border-t border-foreground/10 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2">
              <a
                href="/install"
                className="text-[12px] text-[#004C54] font-semibold hover:underline"
                data-testid="install-walkthrough-help-link"
              >
                Need more help? See the full guide &rarr;
              </a>
              <button
                type="button"
                onClick={() => {
                  setWalkthroughOpen(false);
                  dismissForever();
                }}
                className="inline-flex items-center justify-center rounded-full bg-[#004C54] hover:bg-[#003a40] text-white text-[12.5px] font-semibold px-4 h-9 transition-colors"
                data-testid="install-walkthrough-done"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPromptBanner;
